import { isOnline } from '@const/env';
import { useCallback, useEffect } from 'react';
import { queryUserNotify, updateUserNotify } from '@/services/user';
import { useDebugStore } from '@/stores/debug';
import { useUserNotifyStore } from '@/stores/userNotify';

interface UseNotifyOptions {
  /** 是否获取最新状态 */
  fetchLatest?: boolean;
}

/**
 * 简单的通知状态 Hook
 * 用于获取和更新单个通知状态
 */
export function useNotify(notifyName: string, options: UseNotifyOptions = {}) {
  const { fetchLatest = false } = options;

  // 仅在非线上环境支持 debug override，避免影响真实用户体验
  const notifyOverride = useDebugStore(
    state => (!isOnline ? state.notifyOverrides?.[notifyName] : undefined),
  );
  const notifies = useUserNotifyStore(state => state.notifies);
  const updateNotifyState = useUserNotifyStore(state => state.updateNotifyState);

  const value = notifyOverride ?? notifies[notifyName] ?? false;

  const setValue = useCallback(async (newValue: boolean) => {
    const prevValue = useUserNotifyStore.getState().notifies[notifyName] ?? false;
    // 乐观更新
    updateNotifyState(notifyName, newValue);

    try {
      await updateUserNotify(notifyName);
    } catch (err) {
      console.error('Failed to update notify:', err);
      // 回滚
      updateNotifyState(notifyName, prevValue);
      throw err;
    }
  }, [notifyName, updateNotifyState]);

  // 获取最新状态
  useEffect(() => {
    if (!fetchLatest) {
      return;
    }

    const fetchNotify = async () => {
      try {
        const res = await queryUserNotify(notifyName);
        updateNotifyState(notifyName, res.data.needNotified);
      } catch (err) {
        console.error('Failed to fetch notify:', err);
      }
    };

    fetchNotify();
  }, [fetchLatest, notifyName, updateNotifyState]);

  return [value, setValue] as const;
}
